import { AsyncRenderer } from '@client/new/components/AsyncRenderer/AsyncRenderer';
import {
  WorkoutNotification,
  WorkoutsViewModel,
  listTitles,
  useWorkoutsViewModel,
} from './workouts.viewModel';
import { ToggleButtonGroup } from '@client/new/components/ToggleButtonGroup/ToggleButtonGroup';
import { UseWorkoutsModelParams } from './workouts.model';
import {
  Alert,
  Box,
  CircularProgress,
  Snackbar,
  Typography as MuiTypography,
} from '@mui/material';
import { WorkoutTypesEmptyState } from './WorkoutTypesEmptyState';
import { BasicMenu } from '@client/new/components/BasicMenu/BasicMenu';
import { WorkoutStatus } from './workouts.domain';
import { SmartTable } from '../../../components/SmartTable/SmartTable';
import { colors } from '@client/new/components/colors';
import { Link } from 'react-router';
import { SearchInput } from '@design-system/components/searchinput';
import { WorkoutLinkModal } from './WorkoutLinkModal';
import { ConvertToRestDayModal } from '../../workout/components/ConvertToRestDayModal/ConvertToRestDayModal';
import { ConvertToRestDayToast } from '../../workout/components/ConvertToRestDayModal/ConvertToRestDayToast';
import { ScheduleGroups } from '@client/new/features/schedule-groups';
import { DuplicateWorkoutDialog } from '@client/new/features/workout-duplicate';

export const AsyncWorkouts = (p: UseWorkoutsModelParams) => {
  const workoutsViewModel = useWorkoutsViewModel(p);
  return <AsyncRenderer data={workoutsViewModel} renderer={Workouts} />;
};

const Workouts = (p: WorkoutsViewModel) => {
  const render = () => {
    switch (p.mode.type) {
      case 'all-empty':
        return (
          <WorkoutsLists {...p}>
            <WorkoutTypesEmptyState {...p.mode.value} />
          </WorkoutsLists>
        );

      case 'selected-empty':
        return (
          <WorkoutsLists {...p}>
            <MuiTypography variant="h6" sx={{ color: '#8F94A1' }}>
              This list is empty. All {listTitles[p.selectedList]} workouts will
              appear here.
            </MuiTypography>
          </WorkoutsLists>
        );

      case 'loading':
        return (
          <WorkoutsLists {...p}>
            <Box
              className="flex justify-center items-center"
              sx={{ height: '20vh' }}
            >
              <CircularProgress color="info" />
            </Box>
          </WorkoutsLists>
        );

      case 'standard':
        return (
          <WorkoutsLists {...p}>
            <SmartTable {...p.mode.value} />
          </WorkoutsLists>
        );

      case 'scheduled-groups':
        return (
          <WorkoutsLists {...p}>
            <ScheduleGroups {...p.mode.value} />
          </WorkoutsLists>
        );
    }
  };
  return (
    <div className="flex flex-col gap-4">
      {render()}
      <ConvertToRestDayModal
        isOpen={!!p.workoutToConvert}
        workoutTitle={p.workoutToConvert?.orgTitle ?? ''}
        onCancel={() => p.dispatch({ type: 'cancelConvertToRestDay' })}
        onConfirm={() =>
          p.workoutToConvert &&
          p.dispatch({
            type: 'confirmConvertToRestDay',
            payload: p.workoutToConvert,
          })
        }
      />
      <AsyncRenderer
        data={p.linkModal.url}
        renderer={(data) => (
          <WorkoutLinkModal
            isOpen={p.linkModal.isOpen}
            onClose={() => p.dispatch({ type: 'closeLinkModal' })}
            url={data.url}
          />
        )}
      />
      {p.duplicateDialog ? (
        <DuplicateWorkoutDialog {...p.duplicateDialog} />
      ) : null}
    </div>
  );
};

const Nofication = (
  p: WorkoutNotification & Pick<WorkoutsViewModel, 'hideNotification'>,
): JSX.Element | null => {
  switch (p.type) {
    case 'convertToRestDay':
      return (
        <ConvertToRestDayToast
          outcome={p}
          onShown={() => p.hideNotification(p.type)}
        />
      );
    case 'duplicate':
      return (
        <Snackbar
          open
          key={p.type}
          onClose={() => p.hideNotification(p.type)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          autoHideDuration={4000}
        >
          <Alert
            severity={p.success ? 'success' : 'error'}
            sx={{
              background: p.success
                ? colors.successAlertBg
                : colors.warnAlertBg,
              boxShadow: '0px 3px 8px 0px #1D293914, 0px 0px 1px 0px #EAECF0',
            }}
          >
            {p.success ? (
              <div className="flex justify-between items-center gap-8">
                <span>Workout duplicated successfully.</span>
                <Link to={p.url ?? ''}>
                  <MuiTypography
                    variant="body1"
                    color="CaptionText"
                    sx={{ textDecoration: 'underline' }}
                  >
                    See in drafts
                  </MuiTypography>
                </Link>
              </div>
            ) : (
              <span>Workout duplication failed.</span>
            )}
          </Alert>
        </Snackbar>
      );
  }
};

const WorkoutsLists: React.FC<React.PropsWithChildren<WorkoutsViewModel>> = (
  p,
) => {
  return (
    <>
      {p.notifications.map((n) => (
        <Nofication key={n.type} {...n} hideNotification={p.hideNotification} />
      ))}
      <div className="flex items-center justify-between">
        <ToggleButtonGroup<WorkoutStatus>
          toStringValue={(v) => `${v}`}
          toValue={(v) => v as WorkoutsViewModel['selectedList']}
          value={p.selectedList}
          options={p.listOptions}
          onChange={(e, payload) => {
            p.dispatch({ type: 'selectList', payload });
          }}
          newStyle={true}
        />
        <BasicMenu {...p.menuProps} disabled={p.disableMenu} />
      </div>
      <div className="w-[312px]">
        <SearchInput
          value={p.searchText}
          placeholder="Search workout"
          onChange={(e) => p.setSearchText(e.target.value)}
          onClear={() => p.setSearchText('')}
        />
      </div>
      {p.hasUnassignedWorkouts && (
        <Alert severity="warning" sx={{ background: colors.warnAlertBg }}>
          Weeks are not set. Please choose week & day for each workout
        </Alert>
      )}
      <>{p.children}</>
    </>
  );
};
